// isMasterAdmin — single source of truth for "is this logged-in user a
// master admin who can see and manage every organization?"
//
// History: the SPA used to check
//   globalOrganizationId === process.env.REACT_APP_MASTER_ORG_ID
// in several components to gate org-tree visibility, dropdown population,
// and edit forms. That works on cloud builds where the master org's UUID
// is known when the SPA is built — but NOT on offline / field-bootstrapped
// installs, where the master org is created at runtime by the edge's
// seedAdminBootstrap with a fresh UUID. The build-time constant never
// matches, the dropdowns collapse to "only my own org", and the dashboard
// renders empty.
//
// Mirroring the backend's utils.IsMasterAdmin (role.name === "MASTER ADMIN")
// removes the build-time dependency: role assignments are durable DB state
// that survives any provisioning path. The legacy env-var equality is kept
// as an OR-branch so existing cloud deployments that depend on it keep
// working without any redeploy coordination.

import type { UserLoggedInterface } from "../interfaces/User.interface";

const MASTER_ADMIN_ROLE_FALLBACK = "MASTER ADMIN";

/**
 * Returns true when the given role NAME identifies the deployment's
 * master-admin role: either the env-configured name or the backend
 * enum literal "MASTER ADMIN". Shared by the wizard's role guards so
 * they track the exact same resolution as isMasterAdmin below — a
 * mixed-case-only check would miss default-seeded deployments.
 */
export function isMasterRoleName(roleName: string): boolean {
  const masterRoleName = process.env.REACT_APP_ROLE_MASTER_NAME ?? MASTER_ADMIN_ROLE_FALLBACK;
  return roleName === masterRoleName || roleName === MASTER_ADMIN_ROLE_FALLBACK;
}

/**
 * Returns true when the user is a master admin and should see every
 * organization in the tree.
 *
 * Resolution order:
 *   1. user's role.name matches REACT_APP_ROLE_MASTER_NAME (env-configured)
 *   2. user's role.name matches the literal "MASTER ADMIN" (matches the
 *      backend enum and the YourProfileDrawer/Layout precedent)
 *   3. legacy: globalOrganizationId equals REACT_APP_MASTER_ORG_ID
 *      (preserved so existing cloud builds keep working unchanged)
 */
export function isMasterAdmin(
  userLogged: UserLoggedInterface | null | undefined,
  globalOrganizationId?: string | null,
): boolean {
  const roleName = userLogged?.role?.name ?? "";

  if (isMasterRoleName(roleName)) return true;

  if (
    globalOrganizationId !== undefined &&
    globalOrganizationId !== null &&
    globalOrganizationId === process.env.REACT_APP_MASTER_ORG_ID
  ) {
    return true;
  }

  return false;
}

export default isMasterAdmin;
